// Core data models matching the iOS app structure

export interface PropertyReportModel {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  entries: string[]; // Array of entry IDs (matches iOS structure)
  // Note: userId is implicit from collection path: users/{userId}/reports/{reportId}
}

// Enhanced report model with calculated stats for dashboard display
export interface PropertyReportWithStats extends PropertyReportModel {
  entryCount: number;
  imageCount: number;
}

export interface PropertyEntryModel {
  id: string;
  title: string;
  note: string;
  createdAt: Date;
  reportId: string;
  latitude: number;
  longitude: number;
  address?: string; // Human-readable address
  images: ImageWithMetadata[];
  
  // Deprecated fields (keep for backward compatibility)
  photoURL?: string;
  imageMetadata?: ImageMetadata;
}

export interface ImageWithMetadata {
  id: string; // Unique identifier for each image (matches iOS)
  photoURL: string;
  metadata: ImageMetadata;
}

export interface ImageMetadata {
  capturedAt: Date;
  latitude?: number;
  longitude?: number;
  address?: string;
}

// PDF Builder related types
export interface PDFTemplate {
  id: string;
  name: string;
  description: string;
  preview: string;
  features: string[];
  layout: 'single-column' | 'multi-column' | 'grid';
}

// Rich Document Editor types
export interface DocumentImage {
  id: string;
  url: string;
  x: number; // Position in pixels
  y: number;
  width: number; // Size in pixels
  height: number;
  zIndex: number; // Layering order
  alt?: string;
  showMetadata?: boolean; // Whether to display metadata overlay
  realMetadata?: any; // Original image data with metadata from the source
  metadataWidth?: number; // Width of metadata overlay
  metadataHeight?: number; // Height of metadata overlay
}

export interface DocumentTextBlock {
  id: string;
  content: string; // Rich text content (can include HTML/markdown)
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  zIndex: number;
}

export interface DocumentPage {
  id: string;
  width: number; // Page dimensions
  height: number;
  images: DocumentImage[];
  textBlocks: DocumentTextBlock[];
  backgroundColor: string;
}

export interface DocumentContent {
  reportId: string;
  pages: DocumentPage[]; // Rich document pages
  lastModified: Date;
  userId: string;
}

// MARK: - Company System Types

export enum CompanyRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member'
}

export enum RequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface Company {
  id: string;
  name: string;
  code: string; // 6-digit join code
  createdAt: Date;
  ownerId: string;
  members: CompanyMember[];
}

export interface CompanyMember {
  userId: string;
  email: string;
  role: CompanyRole;
  joinedAt: Date;
  displayName?: string;
  profileImageUrl?: string;
}

export interface CompanyJoinRequest {
  id: string;
  companyCode: string;
  userId: string;
  userEmail: string;
  status: RequestStatus;
  requestedAt: Date;
}

export interface SharedReport extends PropertyReportModel {
  originalReportId: string;
  originalOwnerId: string;
  sharedAt: Date;
  sharedBy: string;
  sharerDisplayName?: string;
  sharerEmail?: string;
  sharerProfileImageUrl?: string;
  sharerInitials?: string;
}

export interface UserProfile {
  id: string;
  userId: string;
  email: string;
  displayName?: string;
  profileImageUrl?: string;
  profileImagePath?: string;
  companyId?: string;
  companyRole?: CompanyRole;
  companyName?: string;
  joinedCompanyAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Helper types for company functionality
export interface CompanyInfo {
  id: string;
  name: string;
  role: CompanyRole;
  memberCount: number;
  joinedAt: Date;
}

export interface SharingInfo {
  sharedBy: string;
  sharedAt: Date;
  canUnshare: boolean;
}

// Company Images System
export enum CompanyImageType {
  LOGO = 'logo',
  HEADER = 'header',
  SIGNATURE = 'signature',
  WATERMARK = 'watermark',
  LETTERHEAD = 'letterhead',
  OTHER = 'other'
}

export interface CompanyImage {
  id: string;
  companyId: string;
  fileName: string; // Changed from imageName to match iOS
  originalFileName: string; // Added to match iOS
  url: string; // Changed from imageUrl to match iOS
  storagePath: string; // Changed from imagePath to match iOS
  imageType: CompanyImageType; // This maps to iOS 'category' field
  uploadedAt: Date; // Changed from uploadDate to match iOS
  updatedAt: Date; // Added to match iOS
  uploadedBy: string; // Changed from createdBy to match iOS
  uploadedByName?: string; // Changed from createdByName to match iOS
  uploadedByEmail?: string; // Added to match iOS
  fileSize: number; // In bytes
  width?: number; // Changed from dimensions to match iOS
  height?: number; // Changed from dimensions to match iOS
  mimeType: string;
  isActive: boolean; // For soft delete functionality
  description?: string; // Added to match iOS
  tags: string[]; // Added to match iOS
  order: number; // Added to match iOS
}

export interface CompanyImageUploadData {
  fileName: string; // Changed from imageName to match iOS
  imageType: CompanyImageType;
  file: File;
  description?: string; // Added to match iOS
  tags?: string[]; // Added to match iOS
}

export interface PDFCustomization {
  primaryColor: string;
  secondaryColor: string;
  logo?: string;
  fontFamily: string;
  fontSize: number;
  includeTableOfContents: boolean;
  includeCoverPage: boolean;
  includeExecutiveSummary: boolean;
}

// PDF Editor types for PowerPoint-style editing
export interface CanvasElement {
  id: string;
  type: 'text' | 'image' | 'shape' | 'table' | 'chart';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  // Type-specific properties
  properties: TextProperties | ImageProperties | ShapeProperties | TableProperties | ChartProperties;
}

export interface TextProperties {
  content: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  align: 'left' | 'center' | 'right' | 'justify';
  lineHeight: number;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

export interface ImageProperties {
  src: string;
  alt: string;
  borderRadius: number;
  opacity: number;
  borderColor?: string;
  borderWidth?: number;
  shadowColor?: string;
  shadowBlur?: number;
}

export interface ShapeProperties {
  shapeType: 'rectangle' | 'circle' | 'triangle' | 'line' | 'arrow';
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  cornerRadius?: number;
}

export interface TableProperties {
  data: string[][];
  headers: string[];
  borderColor: string;
  borderWidth: number;
  headerBackgroundColor: string;
  headerTextColor: string;
  cellBackgroundColor: string;
  cellTextColor: string;
  fontSize: number;
}

export interface ChartProperties {
  chartType: 'bar' | 'line' | 'pie' | 'doughnut';
  data: ChartData[];
  title: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  colors: string[];
}

export interface ChartData {
  label: string;
  value: number;
}

export interface PDFPage {
  id: string;
  elements: CanvasElement[];
  width: number;
  height: number;
  backgroundColor: string;
  backgroundImage?: string;
}

export interface PDFDocument {
  id: string;
  title: string;
  pages: PDFPage[];
  reportId?: string;
  createdAt: Date;
  updatedAt: Date;
  template?: string;
}

export interface PDFEditorState {
  currentDocument: PDFDocument | null;
  currentPageIndex: number;
  selectedElementIds: string[];
  clipboardElements: CanvasElement[];
  history: PDFDocument[];
  historyIndex: number;
  zoom: number;
  gridEnabled: boolean;
  snapToGrid: boolean;
  isLoading: boolean;
  isExporting: boolean;
  exportStatus: string | null;
  error: string | null;
  templates: PDFTemplate[];
  reportData: PropertyReportModel | null;
  availableAssets: {
    images: ImageWithMetadata[];
    entries: PropertyEntryModel[];
  };
}

// Dashboard related types
export interface DashboardStats {
  totalReports: number;
  thisMonthReports: number;
  recentActivity: RecentActivity[];
}

export interface RecentActivity {
  id: string;
  type: 'report_created' | 'report_updated' | 'pdf_generated';
  reportTitle: string;
  timestamp: Date;
}

// Filter and search types
export interface ReportFilter {
  searchTerm: string;
  dateRange: {
    start?: Date;
    end?: Date;
  };
  sortBy: 'title' | 'createdAt' | 'updatedAt';
  sortOrder: 'asc' | 'desc';
}

// User authentication types
export interface User {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
}

// Redux store types
export interface RootState {
  auth: AuthState;
  reports: ReportsState;
  dashboard: DashboardState;
  pdfEditor: PDFEditorState;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

export interface ReportsState {
  reports: PropertyReportModel[];
  selectedReport: PropertyReportModel | null;
  isLoading: boolean;
  error: string | null;
  filter: ReportFilter;
}

export interface DashboardState {
  stats: DashboardStats | null;
  isLoading: boolean;
  error: string | null;
}
